import { Button } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import GetAppIcon from '@material-ui/icons/GetApp';
import LayersIcon from '@material-ui/icons/Layers';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import SettingsIcon from '@material-ui/icons/Settings';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';

import { Flex } from '@/components/Flex';
import { useStores } from '@/hooks/useStores';
import {
  fetchFlags,
  LD_FLAG_NEW_FEATURE,
} from '@/utils/fetchLaunchDarklyFlags';
import {
  NEW_FEATURE_FLAG_DESIGNER,
  NEW_FEATURE_FLAG_PRODUCTS_CREATE,
  NEW_FEATURE_FLAG_SETTINGS,
} from '@/constants/newFeatureFlags';
import EmptyCard from '@/components/EmptyCard';
import EmptyPage from '@/components/Layouts/EmptyPage';
import Link from '@/components/Link';
import theme from '@/utils/styles/theme';

const StyledIcon = styled.div`
  background-color: white;
  align-items: center;
  border-radius: 50%;
  display: flex;
  height: 64px;
  width: 64px;
  justify-content: center;

  svg {
    font-size: 32px;
    color: ${({ theme }) => theme.colors.neutrals[8]};
  }
`;

const Title = styled.div`
  font-size: 48px;
  font-weight: bold;
  margin-top: 30px;
  color: white;
`;

const Subtitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  margin-top: 10px;
  max-width: 600px;
  opacity: 0.8;
  color: white;
  font-weight: 500px;
  margin-bottom: 38px;
`;

const OtherThingsToTry = styled.div`
  color: ${({ theme }) => theme.colors.whites[6]};
  padding-top: 40px;
  padding-bottom: 20px;
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  font-weight: bold;
  letter-spacing: 1px;
`;

const EmptyProducts = () => {
  const { allProductsPage } = useStores();
  const { user } = useAuth0();
  const ldClient = useLDClient();
  const [newFeaturesEnabled, setNewFeaturesEnabled] = useState({});

  useEffect(() => {
    const fetchFlagsAsync = async () => {
      const result = await fetchFlags(ldClient, user);

      if (result !== undefined && result[LD_FLAG_NEW_FEATURE] !== undefined) {
        const newFeatures = {};
        result[LD_FLAG_NEW_FEATURE].forEach((f) => {
          newFeatures[f] = true;
        });
        setNewFeaturesEnabled(newFeatures);
      }
    };
    fetchFlagsAsync();
  }, [ldClient, user]);

  const headerActions = [
    {
      alwaysVisible: true,
      icon: <AddCircleIcon />,
      linkTo: '/admin/products/create',
      redirectPath: '/product/add',
      redirect: !newFeaturesEnabled[NEW_FEATURE_FLAG_PRODUCTS_CREATE],
      type: 'button',
      label: 'Add',
    },
    {
      icon: <GetAppIcon />,
      onClick: () => {
        allProductsPage.openProductImportModal();
      },
      dataTestId: 'product-list-page-header-import',
      label: 'Import Products',
      type: 'button',
    },
  ];

  const otherActions = [
    {
      title: 'Set Up Payments and Shipping',
      subtitle: 'Make sure your checkout is ready.',
      icon: <SettingsIcon />,
      to: '',
      redirectPath: '/settings/detail',
      redirect: !newFeaturesEnabled[NEW_FEATURE_FLAG_SETTINGS],
    },
    {
      title: 'Customize Your Storefront',
      subtitle: 'Visit Site Designer to get started.',
      icon: <LayersIcon />,
      to: '',
      redirectPath: '/designer',
      redirect: !newFeaturesEnabled[NEW_FEATURE_FLAG_DESIGNER],
    },
  ];

  return (
    <EmptyPage
      pageTitle="Products"
      headerActions={headerActions}
      backgroundColor={theme.colors.neutrals[8]}
      helpText="We can help you get your first sale."
    >
      <StyledIcon>
        <LocalOfferIcon />
      </StyledIcon>
      <Title>Start Adding Products</Title>
      <Subtitle>Add, import, and edit all product details here.</Subtitle>
      <Link
        href="/admin/products/create"
        redirectPath="/product/add"
        redirect={!newFeaturesEnabled[NEW_FEATURE_FLAG_PRODUCTS_CREATE]}
      >
        <Button size="small" color="primary">
          Add a Product
        </Button>
      </Link>
      {otherActions.length > 0 && (
        <>
          <OtherThingsToTry>Other Things to Try</OtherThingsToTry>
          <Flex>
            {otherActions.map((action) => (
              <EmptyCard key={`${action.title}`} {...action} />
            ))}
          </Flex>
        </>
      )}
    </EmptyPage>
  );
};

export default withLDProvider({
  clientSideID: process.env.NEXT_PUBLIC_LAUNCH_DARKLY_ID,
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
})(EmptyProducts);
